import sortBy from "lodash.sortby";
import { useEffect, useState } from "react";
import { match, P } from "ts-pattern";
import { useLocalStorage } from "usehooks-ts";
import { BaseButton } from "@/components/atomic/BaseButton";
import { Dialog, DialogContent, DialogOverlay, DialogTitle } from "@/components/atomic/Dialog";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import Separator from "@/components/atomic/Separator";
import Button from "@/components/ui/Button";
import { useCategoryTree } from "@/hooks/useCategoryTree";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { Analysis } from "@/models/analysis";
import { DisplayGroup } from "@/models/displayGroup";

const Column: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  return <div className="flex flex-col gap-5 pr-5 min-w-[220px]">{children}</div>;
};

const Item: React.FC<
  {
    name: string;
    value: string;
    selected: boolean;
    isFirst: boolean;
    handleItemClick: (value: string) => void;
  }
> = (
  { name, value, isFirst, selected, handleItemClick },
) => {
  return (
    <BaseButton
      variant="ghost"
      className={cn(
        "block w-[200px] h-10 pl-5 justify-start text-[#5F6368] hover:bg-[#6888E1] hover:text-white hover:font-bold truncate text-left",
        isFirst && "mt-5",
        selected && "bg-[#6888E1] text-white font-bold",
      )}
      onClick={() => handleItemClick(value)}
      title={name}
    >
      {name}
    </BaseButton>
  );
};

interface Props {
  className?: string;
  analysis: Analysis;
  displayGroups: DisplayGroup[];
  initialDisplayGroup?: DisplayGroup;
  initialCategoryPath?: string;
  isOpenModal: boolean;
  setIsOpenModal: (value: boolean) => void;
  submit: (displayGroupId: string, categoryPath: string) => void;
}

const CategorySelectModal: React.FC<Props> = (
  {
    className = "",
    analysis,
    displayGroups,
    isOpenModal,
    initialDisplayGroup,
    initialCategoryPath,
    setIsOpenModal,
    submit,
  },
) => {
  const [isSideBarOpen] = useLocalStorage("isSideBarOpen", true);
  const { categoryTree } = useCategoryTree(analysis?.categoryTreeId);
  const { categoryMaxLevel } = useWorkspaces();

  // 0番目だけ表示グループIDが入り、1番目以降はカテゴリパスが入る
  const [selectedCategories, setSelectedCategories] = useState<string[]>(new Array(categoryMaxLevel).fill(""));
  useEffect(() => {
    setSelectedCategories(new Array(categoryMaxLevel).fill(""));
  }, [categoryMaxLevel]);

  useEffect(() => {
    if (!initialDisplayGroup || !initialCategoryPath) return;

    // 選択されたカテゴリが現在の分析のカテゴリツリーに存在するかチェック
    const categories = selectedCategories.map((v, i) => {
      if (i === 0) return initialDisplayGroup.id;
      if (i > 0) return `/${initialCategoryPath.split("/").slice(1, i + 1).join("/")}/`;
      return v;
    });
    setSelectedCategories(categories);
  }, [initialDisplayGroup?.id, initialCategoryPath]);

  const isSubmitDisabled = selectedCategories.includes("");
  const currentLayer = isSubmitDisabled
    ? selectedCategories.indexOf("")
    : categoryMaxLevel;

  const getItems = (layer: number) => {
    // 0層の場合表示グループのリストを返す
    if (layer === 0) {
      return initialDisplayGroup // initialDisplayGroupがある場合は他の表示グループを選択できないようにする
        ? [{ name: initialDisplayGroup.name ?? "", value: initialDisplayGroup.id }]
        : displayGroups?.map((dg) => ({ name: dg.name, value: dg.id })) ?? [];
    }

    // 現在選択しようとしている層より下の層は[]を返す
    if (layer > currentLayer) return [];

    const displayGroup = displayGroups?.find((dg) => dg.id === selectedCategories[0]);
    if (!displayGroup) return [];

    // 現在選択しようとしている層の1個上で選択中のカテゴリパスを取得
    const selectedCategoryPath = selectedCategories[layer - 1];

    const items = categoryTree?.categories.filter((c) =>
      c.level === layer // 選択しようとしている層のカテゴリのみ
      && c.key.startsWith(layer === 1 ? "/" : selectedCategoryPath) // 1個上の層で選択中のカテゴリパスから始まるカテゴリのみ
      && !!displayGroup?.categoryPaths.find((path) => path.startsWith(c.key)) // 表示グループに含まれるカテゴリのみ
    ).map((c) => ({ name: c.name, value: c.key })) ?? [];

    return sortBy(items, ["name"]);
  };

  const handleClickItem = (layer: number, value: string) => {
    // 選択した層より下の層は""にリセット
    const categories = selectedCategories.map((c, i) =>
      match(i)
        .with(P.when(n => n < layer), () => c)
        .with(P.when(n => n === layer), () => value)
        .otherwise(() => "")
    );
    setSelectedCategories(categories);
  };

  const handleClickSubmit = () => {
    const selectedDisplayGroupId = selectedCategories[0];

    setIsOpenModal(false);
    const selectedCategoryPath = selectedCategories.at(-1);
    if (selectedCategoryPath) {
      submit(selectedDisplayGroupId, selectedCategoryPath);
    }
  };

  return (
    <Dialog open={isOpenModal} onOpenChange={setIsOpenModal}>
      <DialogOverlay
        className={cn(
          "bg-[#FFFFFFE6] top-[100px]",
          isSideBarOpen ? "left-[260px]" : "left-[60px]",
        )}
      />
      <DialogContent
        className={cn(
          "top-[130px] translate-0 max-w-[1110px]",
          isSideBarOpen ? "left-[210px] w-[calc(100vw-240px)]" : "left-[90px] w-[calc(100vw-120px)]",
          className,
        )}
      >
        <DialogTitle className="flex flex-row justify-between mb-7">
          <div className="leading-[40px]">カテゴリーの選択</div>
          <Button
            className="w-50 h-10 text-base text-[#5F6368]"
            disabled={isSubmitDisabled}
            onClick={handleClickSubmit}
          >
            決定
          </Button>
        </DialogTitle>
        <Separator />
        <ScrollArea>
          <div className="flex gap-5">
            {selectedCategories.map((selected, i) => (
              <div className="flex" key={selected + i}>
                <ScrollArea className="h-[calc(100vh-360px)]">
                  <Column>
                    {getItems(i).map((item, j) => (
                      <Item
                        key={item.value}
                        name={item.name}
                        value={item.value}
                        isFirst={j === 0}
                        selected={item.value === selectedCategories[i]}
                        handleItemClick={(value: string) => handleClickItem(i, value)}
                      />
                    ))}
                  </Column>
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
                <Separator orientation="vertical" />
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="hidden" />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CategorySelectModal;
